/**
 * User permission utilities for checking user roles and access levels
 *
 * Role hierarchy:
 *   01 - Super Admin     (CompanyID 1 only) — full system access
 *   02 - Client Admin    — manage own company's data, users, projects, hardware
 *   03 - Region Admin    — manage own region's projects and data
 *   04 - Project Admin   — manage assigned projects
 *   05 - Client Viewer   — view own company's data (read-only)
 *   06 - Region Viewer   — view own region's data (read-only)
 *   07 - Project Viewer  — view assigned projects (read-only)
 *   11 - Calibration Admin — manage calibrations
 *   99 - Specialised User — no login access
 */

export interface User {
  usertype?: string;
  UserType?: string;
  companyid?: string;
  CompanyId?: string;
  CompanyID?: string;
  /** RBAC permission strings served by /auth/me (backend utils/rbac.py). */
  permissions?: string[];
  [key: string]: any;
}

// ── RBAC permissions (source of truth: backend utils/rbac.py) ──────────
//
// The backend computes the user's permissions from the reviewed User
// Functions matrix and serves them on /auth/me. Frontend code should gate
// UI on hasPermission(); the role helpers below remain only for legacy
// call sites and for users cached in localStorage before this rollout.

export const hasPermission = (
  user: User | null | undefined,
  permission: string
): boolean => {
  return Array.isArray(user?.permissions) && user!.permissions!.includes(permission);
};

// ── Helper: normalise user fields ──────────────────────────────────────

export const getUserType = (user: User | null | undefined): string => {
  if (!user) return '';
  return user.usertype || user.UserType || '';
};

/** CompanyID is an opaque string: a GUID for every client, "1" for
 *  HDTECH. Never parseInt it ("92a8ff12-..." would become 92). Older
 *  /auth/me replies sent companyid as a number, so prefer CompanyID. */
export const getUserCompanyId = (user: User | null | undefined): string => {
  if (!user) return '';
  return String(user.CompanyID || user.companyid || user.CompanyId || '');
};

// ── Role checks ────────────────────────────────────────────────────────

/** Super Admin: UserType 01 AND CompanyID 1. The only role check kept:
 *  super-admin-only surfaces (Clients, simulator, licensing) have no rows
 *  in the RBAC matrix. Everything else must gate on hasPermission(). */
export const isSuperAdmin = (user: User | null | undefined): boolean => {
  if (!user) return false;
  return getUserType(user) === '01' && String(getUserCompanyId(user)) === '1';
};

// ── Sidebar menu visibility ────────────────────────────────────────────

export type MenuKey =
  | 'controlPanel'
  | 'hardware'
  | 'projects'
  | 'products'
  | 'suppliers'
  | 'users'
  | 'companies'
  | 'settings'
  | 'maturityInputs'
  | 'reports'
  | 'temperatureLimits'
  | 'maturityTargets'
  | 'evaporationLimit'
  | 'environmentalLimits'
  | 'simulator'
  | 'help';

// Menu entries gated by an RBAC permission. Companies (Clients page), the
// simulator, and the standalone Users page stay super-admin only, below —
// clients reach their user list via the Users tab inside Settings.
const MENU_PERMISSIONS: Array<[MenuKey, string]> = [
  ['hardware', 'hardware.view_list'],
  ['projects', 'projects.view'],
  ['products', 'mix_designs.view'],
  ['suppliers', 'batch_plants.view_list'],
  ['settings', 'settings.view_client_details'],
  ['maturityInputs', 'maturity_inputs.view_page'],
  ['reports', 'reports.generate'],
  ['temperatureLimits', 'temp_limits.view_page'],
  ['maturityTargets', 'strength_targets.view_page'],
  ['evaporationLimit', 'evap_limits.view_page'],
  // Environmental (temp & humidity sensor) limits: placeholder page, gated
  // like evaporation limits until rbac.py grows a dedicated permission.
  ['environmentalLimits', 'evap_limits.view_page'],
];

/**
 * Returns the set of menu keys the current user is allowed to see.
 *
 * When the user object carries `permissions` (served by /auth/me), menus
 * are derived from the RBAC matrix. The role switch below is only the
 * fallback for users cached in localStorage before permissions existed;
 * AuthContext refreshes the user on load, so the fallback is short-lived.
 */
export const getVisibleMenuKeys = (user: User | null | undefined): Set<MenuKey> => {
  const type = getUserType(user);

  // Always visible
  const keys = new Set<MenuKey>(['controlPanel', 'help']);

  if (!type) return keys;

  if (Array.isArray(user?.permissions)) {
    for (const [menuKey, permission] of MENU_PERMISSIONS) {
      if (hasPermission(user, permission)) keys.add(menuKey);
    }
    if (isSuperAdmin(user)) {
      keys.add('users');
      keys.add('companies');
      keys.add('simulator');
    }
    return keys;
  }

  switch (type) {
    case '01': // Super Admin
      // Everything
      keys.add('hardware');
      keys.add('projects');
      keys.add('products');
      keys.add('suppliers');
      keys.add('users');
      keys.add('companies');
      keys.add('settings');
      keys.add('maturityInputs');
      keys.add('reports');
      keys.add('temperatureLimits');
      keys.add('maturityTargets');
      keys.add('evaporationLimit');
      keys.add('environmentalLimits');
      keys.add('simulator');
      break;

    case '02': // Client Admin (users live in the Settings tab, not the menu)
      keys.add('hardware');
      keys.add('projects');
      keys.add('products');
      keys.add('suppliers');
      keys.add('settings');
      keys.add('maturityInputs');
      keys.add('temperatureLimits');
      keys.add('maturityTargets');
      keys.add('evaporationLimit');
      keys.add('environmentalLimits');
      break;

    case '03': // Region Admin
    case '04': // Project Admin
      keys.add('hardware');
      keys.add('projects');
      keys.add('products');
      keys.add('suppliers');
      keys.add('settings');
      keys.add('maturityInputs');
      keys.add('temperatureLimits');
      keys.add('maturityTargets');
      keys.add('evaporationLimit');
      keys.add('environmentalLimits');
      break;

    case '11': // Calibration Admin
      keys.add('hardware');
      keys.add('projects');
      keys.add('products');
      keys.add('suppliers');
      keys.add('settings');
      keys.add('maturityInputs');
      keys.add('temperatureLimits');
      keys.add('maturityTargets');
      keys.add('evaporationLimit');
      keys.add('environmentalLimits');
      break;

    case '05': // Client Viewer
    case '06': // Region Viewer
    case '07': // Project Viewer
      keys.add('hardware');
      keys.add('projects');
      keys.add('products');
      keys.add('suppliers');
      break;
  }

  return keys;
};

/**
 * User types that the current user is allowed to assign when creating/editing users.
 * - Super Admin can assign any type
 * - Client Admin can assign types within their company (not Super Admin)
 * - Others cannot create users
 */
export const getAllowedUserTypes = (user: User | null | undefined): string[] => {
  if (isSuperAdmin(user)) {
    return ['01', '02', '03', '04', '05', '06', '07', '11'];
  }
  if (getUserType(user) === '02') {
    // Client Admin — can create admins and viewers but NOT super admin
    return ['02', '03', '04', '05', '06', '07', '11'];
  }
  return [];
};

/**
 * User types visible in the filter dropdown (Users list page).
 * Non-super-admins shouldn't see / filter by Super Admin.
 */
export const getFilterableUserTypes = (user: User | null | undefined): Array<{ value: string; label: string }> => {
  const all = [
    { value: '01', label: 'Super Admin' },
    { value: '02', label: 'Client Admin' },
    { value: '03', label: 'Region Admin' },
    { value: '04', label: 'Project Admin' },
    { value: '05', label: 'Client Viewer' },
    { value: '06', label: 'Region Viewer' },
    { value: '07', label: 'Project Viewer' },
    { value: '11', label: 'Calibration Admin' },
  ];

  if (isSuperAdmin(user)) return all;

  // Non-super-admins don't see the Super Admin filter option
  return all.filter(t => t.value !== '01');
};
